import React, { useEffect, useRef } from 'react';
import { Box, Button, Container, Typography, Chip, Stack } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ShieldIcon from '@mui/icons-material/Shield';
import SpeedIcon from '@mui/icons-material/Speed';

function PingDot({ top, left, delay = 0, color = '#1075BA' }) {
  return (
    <Box sx={{ position: 'absolute', top, left }}>
      <Box sx={{
        width: 14, height: 14, borderRadius: '50%', bgcolor: color,
        boxShadow: `0 0 0 0 ${color}`,
        animation: `ping 2s ${delay}s infinite`,
        '@keyframes ping': {
          '0%': { boxShadow: `0 0 0 0 ${color}88` },
          '70%': { boxShadow: `0 0 0 18px ${color}00` },
          '100%': { boxShadow: `0 0 0 0 ${color}00` },
        },
      }} />
    </Box>
  );
}

function FloatingBadge({ icon, label, value, sx = {} }) {
  return (
    <Box sx={{
      position: 'absolute',
      bgcolor: '#fff',
      borderRadius: '14px',
      px: 2, py: 1.5,
      boxShadow: '0 8px 32px rgba(16,117,186,0.15)',
      display: 'flex', alignItems: 'center', gap: 1.5,
      border: '1px solid rgba(16,117,186,0.1)',
      animation: 'floatBadge 3s ease-in-out infinite',
      ...sx,
    }}>
      {icon}
      <Box>
        <Typography sx={{ fontSize: '0.65rem', color: '#4a6580', fontWeight: 600, lineHeight: 1 }}>{label}</Typography>
        <Typography sx={{ fontSize: '0.85rem', color: '#0d1f35', fontWeight: 700, lineHeight: 1.4 }}>{value}</Typography>
      </Box>
    </Box>
  );
}

export default function Hero() {
  return (
    <Box
      sx={{
        minHeight: '100vh',
        pt: { xs: 12, md: 10 },
        pb: 10,
        position: 'relative',
        overflow: 'hidden',
        background: 'linear-gradient(160deg, #eaf4ff 0%, #f5f9ff 40%, #fffbf0 100%)',
      }}
    >
      {/* Background blobs */}
      <Box sx={{
        position: 'absolute', top: -200, right: -200,
        width: 700, height: 700, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(16,117,186,0.12) 0%, transparent 70%)',
        pointerEvents: 'none',
      }} />
      <Box sx={{
        position: 'absolute', bottom: -100, left: -150,
        width: 500, height: 500, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(248,178,32,0.1) 0%, transparent 70%)',
        pointerEvents: 'none',
      }} />

      {/* Grid lines subtle */}
      <Box sx={{
        position: 'absolute', inset: 0,
        backgroundImage: 'linear-gradient(rgba(16,117,186,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(16,117,186,0.04) 1px, transparent 1px)',
        backgroundSize: '60px 60px',
        pointerEvents: 'none',
      }} />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 6, alignItems: 'center', minHeight: '85vh' }}>
          {/* Left */}
          <Box sx={{ animation: 'fadeUp 0.8s ease both', '@keyframes fadeUp': { from: { opacity: 0, transform: 'translateY(30px)' }, to: { opacity: 1, transform: 'none' } } }}>
            <Chip
              label="Trusted cloud-based solution"
              sx={{
                mb: 3, bgcolor: 'rgba(16,117,186,0.08)', color: '#1075BA',
                fontWeight: 700, fontSize: '0.8rem', border: '1px solid rgba(16,117,186,0.2)',
              }}
            />

            <Typography variant="h1" sx={{
              fontSize: { xs: '2.6rem', md: '3.8rem' },
              lineHeight: 1.1,
              color: '#0d1f35',
              mb: 2,
            }}>
              Track. Report.
              <Box component="span" sx={{
                display: 'block',
                background: 'linear-gradient(135deg, #1075BA 0%, #3a9de4 60%, #F8B220 100%)',
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
              }}>Optimize.</Box>
            </Typography>

            <Typography variant="body1" sx={{ color: '#4a6580', mb: 4, fontSize: '1.1rem', maxWidth: 480 }}>
            Manage your entire fleet with a hardware-free app for real-time tracking, automated time logs, and instant reporting.
            </Typography>

            <Stack direction="row" gap={2} flexWrap="wrap" sx={{ mb: 4 }}>
              {['Real-time GPS', 'Time in & out', 'Issue Reports'].map((tag) => (
                <Box key={tag} sx={{
                  display: 'flex', alignItems: 'center', gap: 0.75,
                  px: 1.5, py: 0.75, borderRadius: 2, mr: 1,
                  bgcolor: 'rgba(16,117,186,0.06)',
                  border: '1px solid rgba(16,117,186,0.12)',
                }}>
                  <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#1075BA' }} />
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#1075BA' }}>{tag}</Typography>
                </Box>
              ))}
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
              <Button
                variant="contained"
                size="large"
                sx={{
                  background: 'linear-gradient(135deg, #1075BA 0%, #3a9de4 100%)',
                  boxShadow: '0 8px 30px rgba(16,117,186,0.4)',
                  fontSize: '1rem',
                  '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 12px 40px rgba(16,117,186,0.5)' },
                  transition: 'all 0.25s ease',
                }}
              >
                Book a Demo
              </Button>
              {/* <Button
                variant="outlined"
                size="large"
                startIcon={<PlayArrowIcon />}
                sx={{
                  borderColor: 'rgba(16,117,186,0.3)', color: '#1075BA', fontSize: '1rem',
                  '&:hover': { borderColor: '#1075BA', bgcolor: 'rgba(16,117,186,0.04)' },
                }}
              >
                See How It Works
              </Button> */}
            </Stack>

            {/* Stats row */}
            {/* <Stack direction="row" gap={4} sx={{ mt: 5, pt: 4, borderTop: '1px solid rgba(16,117,186,0.1)' }}>
              {[{ num: '15K+', label: 'Vehicles Tracked' }, { num: '99.9%', label: 'Uptime SLA' }, { num: '150+', label: 'Cities Covered' }].map((s) => (
                <Box key={s.num}>
                  <Typography sx={{ fontWeight: 800, fontSize: '1.6rem', color: '#1075BA', lineHeight: 1 }}>{s.num}</Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: '#4a6580', fontWeight: 600 }}>{s.label}</Typography>
                </Box>
              ))}
            </Stack> */}
          </Box>

          {/* Right: map card mockup */}
          <Box sx={{
            position: 'relative', display: { xs: 'none', md: 'flex' }, justifyContent: 'center',
            animation: 'fadeIn 1s 0.3s ease both',
            '@keyframes fadeIn': { from: { opacity: 0, transform: 'scale(0.95)' }, to: { opacity: 1, transform: 'none' } },
          }}>
            {/* Map card */}
            <Box sx={{
              width: 480, height: 480,
              borderRadius: '28px',
              background: 'linear-gradient(145deg, #c8e8ff 0%, #d4f0ff 40%, #e8f7ff 100%)',
              boxShadow: '0 30px 80px rgba(16,117,186,0.2)',
              border: '2px solid rgba(255,255,255,0.8)',
              overflow: 'hidden',
              position: 'relative',
            }}>
              {/* Map grid */}
              <Box sx={{
                position: 'absolute', inset: 0,
                backgroundImage: 'linear-gradient(rgba(16,117,186,0.12) 1px, transparent 1px), linear-gradient(90deg, rgba(16,117,186,0.12) 1px, transparent 1px)',
                backgroundSize: '40px 40px',
              }} />

              {/* Road lines */}
              <Box sx={{ position: 'absolute', top: '30%', left: 0, right: 0, height: 3, bgcolor: 'rgba(255,255,255,0.7)', borderRadius: 2 }} />
              <Box sx={{ position: 'absolute', top: '60%', left: 0, right: 0, height: 3, bgcolor: 'rgba(255,255,255,0.7)', borderRadius: 2 }} />
              <Box sx={{ position: 'absolute', left: '40%', top: 0, bottom: 0, width: 3, bgcolor: 'rgba(255,255,255,0.7)', borderRadius: 2 }} />
              <Box sx={{ position: 'absolute', left: '70%', top: 0, bottom: 0, width: 3, bgcolor: 'rgba(255,255,255,0.7)', borderRadius: 2 }} />

              {/* Ping dots */}
              <PingDot top="28%" left="38%" delay={0} color="#1075BA" />
              <PingDot top="58%" left="68%" delay={0.6} color="#F8B220" />
              <PingDot top="45%" left="55%" delay={1.2} color="#1075BA" />
              <PingDot top="70%" left="30%" delay={1.8} color="#F8B220" />

              {/* Route path SVG */}
              <Box component="svg" sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
                <polyline
                  points="185,144 185,280 330,280 265,215"
                  fill="none"
                  stroke="#1075BA"
                  strokeWidth="3"
                  strokeDasharray="8 5"
                  opacity="0.6"
                />
              </Box>

              {/* Center car icon */}
              <Box sx={{
                position: 'absolute', top: '42%', left: '50%', transform: 'translate(-50%,-50%)',
                width: 50, height: 50, borderRadius: '50%',
                background: 'linear-gradient(135deg, #1075BA, #3a9de4)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 8px 24px rgba(16,117,186,0.45)',
                animation: 'pulse 2s ease-in-out infinite',
                '@keyframes pulse': { '0%,100%': { transform: 'translate(-50%,-50%) scale(1)' }, '50%': { transform: 'translate(-50%,-50%) scale(1.08)' } },
              }}>
                <Box component="span" sx={{ color: '#fff', fontSize: '1.4rem' }}>🚗</Box>
              </Box>

              {/* Dark overlay card at bottom */}
              <Box sx={{
                position: 'absolute', bottom: 0, left: 0, right: 0,
                background: 'linear-gradient(to top, rgba(13,31,53,0.9) 0%, transparent 100%)',
                p: 3, pt: 6,
              }}>
                <Typography sx={{ color: '#fff', fontWeight: 700, mb: 0.5 }}>Vehicle #FL-2047</Typography>
                <Stack direction="row" gap={2}>
                  <Box>
                    <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.7rem' }}>Speed</Typography>
                    <Typography sx={{ color: '#F8B220', fontWeight: 700 }}>62 km/h</Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.7rem' }}>Location</Typography>
                    <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.85rem' }}>EDSA, Mandaluyong</Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.7rem' }}>Status</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#4ade80', animation: 'ping 2s infinite' }} />
                      <Typography sx={{ color: '#4ade80', fontWeight: 700, fontSize: '0.85rem' }}>Live</Typography>
                    </Box>
                  </Box>
                </Stack>
              </Box>
            </Box>

            {/* Floating badges */}
            <FloatingBadge
              icon={<ShieldIcon sx={{ color: '#1075BA', fontSize: 22 }} />}
              label="Geofence Alert"
              value="Zone Breached"
              sx={{ top: -20, right: -10 }}
            />
            <FloatingBadge
              icon={<SpeedIcon sx={{ color: '#F8B220', fontSize: 22 }} />}
              label="Driver Score"
              value="94 / 100"
              sx={{ bottom: 80, left: -30, animationDelay: '1s' }}
            />
            <FloatingBadge
              icon={<LocationOnIcon sx={{ color: '#ef4444', fontSize: 22 }} />}
              label="Issue Report"
              value="Tire Pressure Low"
              sx={{ bottom: -20, right: 20, animationDelay: '2s' }}
            />
          </Box>
        </Box>
      </Container>

      <style>{`
        @keyframes floatBadge {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-8px); }
        }
      `}</style>
    </Box>
  );
}
